import type { BuddyConfigurationPeople } from "./buddy-config";
import type { BuddyAutomationConfig } from "@novemai/onboarding-sdk";
// B5 — typed fetch wrappers for the onboarding surfaces. Backend contract:
// services/platform-graph/src/routes/onboarding.ts.
//
// The route carries no `schema:` block, so the generated `paths[...]` entries
// document neither the query nor the response. Each call is cast to its
// documented contract through the SAME `platformGraph.<VERB> as unknown as
// <Typed>` pattern the approval-chains / employees helpers established — the
// casts collapse on their own once the route grows a schema block.
//
// Both list routes already emit the canonical camelCase `{ items, nextCursor }`
// keyset page, so no cursor-field remapping is needed here (approval-chains has
// to remap `next_cursor`).

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import type { KeysetPage } from "@/api/keyset";

// --------------------------------------------------------------------------
// Wire shapes (mirror PlanResponseRow / InstanceResponseRow / TaskResponseRow).
// --------------------------------------------------------------------------

/** The stored selector, mirroring @novemai/onboarding-sdk SelectionCriteria.
 *  Re-declared rather than imported: the SDK is a backend dependency and
 *  pulling it in for one type would put it in the browser bundle. */
export interface PlanSelectionCondition {
  readonly attribute: string;
  readonly operator: string;
  readonly value: string | readonly string[];
}

export interface PlanSelectionCriteria {
  readonly all?: readonly PlanSelectionCondition[];
  readonly any?: readonly PlanSelectionCondition[];
  readonly not?: readonly PlanSelectionCondition[];
}

export interface OnboardingPlanRow {
  readonly plan_id: string;
  readonly name: string;
  readonly description: string | null;
  readonly status: string;
  readonly version: number;
  readonly precedence: number;
  readonly selection_criteria: PlanSelectionCriteria;
  readonly buddy_policy: string;
  readonly buddy_automation?: BuddyAutomationConfig | null;
  readonly buddy_people?: BuddyConfigurationPeople;
  readonly preboarding_enabled?: boolean;
  readonly preboarding_start_offset_days?: number | null;
  readonly ramp_window_days: number;
}

export type ManagementLevel = "manager" | "individual_contributor";

export interface PlanMatchResult {
  readonly matches: readonly OnboardingPlanRow[];
  readonly management_level: ManagementLevel;
  readonly department_id: string | null;
  /** Published plans the server evaluated. Zero means the tenant has no
   *  published plan at all — a different problem from "none match". */
  readonly considered_count: number;
}

export interface OnboardingInstanceRow {
  readonly instance_id: string;
  readonly employee_id: string;
  /** Resolved server-side (SEM-301); null when the subject is tombstoned. */
  readonly employee_name: string | null;
  readonly manager_id: string;
  readonly plan_ids: readonly string[];
  readonly status: string;
  readonly start_date: string;
  readonly ramp_ends_at: string;
  /** Decimal STRING 0–100 — a completion ratio, never money. */
  readonly completion_percent: string;
  readonly at_risk: boolean;
}

export interface OnboardingTaskRow {
  readonly form_bound?: boolean;
  readonly training?: { readonly course_id: string; readonly assignment_id: string | null; readonly state: "waiting" | "assigned" | "completed" | "unavailable" };
  readonly task_id: string;
  readonly instance_id: string;
  readonly template_task_key: string;
  readonly title: string;
  readonly kind: string;
  readonly owner_kind: string;
  readonly assignee_id: string | null;
  readonly milestone_key: string;
  readonly due_at: string;
  readonly opens_at: string;
  readonly status: string;
  readonly required: boolean;
  readonly completed_at: string | null;
}

export interface OnboardingMilestone {
  readonly key: string;
  readonly label: string;
  readonly day_offset: number;
}

/** One blueprint task on the wire — the shape POST/PATCH accept and
 *  GET /plans/:id echoes back (the route maps the stored camelCase form
 *  explicitly so the round trip is not lossy). */
export interface OnboardingTaskSpecWire {
  readonly training_source?: { readonly course_id: string };
  readonly form_source?: { readonly app_id: string; readonly version_id: string; readonly form_id: string };
  readonly template_task_key: string;
  readonly title: string;
  readonly description?: string;
  readonly kind: string;
  readonly owner_kind: string;
  readonly milestone_key: string;
  readonly opens_at_offset_days: number;
  readonly due_at_offset_days: number;
  readonly dependency_keys?: readonly string[];
  readonly required: boolean;
  readonly preboarding_enabled?: boolean;
  readonly compliance_tag?: string;
  readonly locale?: string;
}

export interface OnboardingPlanDetail {
  readonly plan: OnboardingPlanRow;
  readonly milestones: readonly OnboardingMilestone[];
  readonly task_specs: readonly OnboardingTaskSpecWire[];
}

export interface OnboardingBuddyPairing {
  readonly pairing_id: string;
  readonly new_hire_id: string;
  readonly buddy_id: string;
  readonly status: string;
  readonly suggested_cadence_days: number;
  readonly match_rationale: string | null;
}

/** GET /v1/onboarding/instances/:id — the instance PLUS everything the detail
 *  screen renders, in one round trip (the route resolves tasks, the open buddy
 *  pairing and the plan's milestone list itself). */
export interface OnboardingInstanceDetail {
  readonly instance: OnboardingInstanceRow;
  readonly tasks: readonly OnboardingTaskRow[];
  readonly pairing: OnboardingBuddyPairing | null;
  readonly milestones: readonly OnboardingMilestone[];
  readonly plan_buddy_policy: string | null;
}

// --------------------------------------------------------------------------
// Typed-cast call signatures.
// --------------------------------------------------------------------------

type PlansGet = (
  path: "/v1/onboarding/plans",
  init: { params: { query: { cursor?: string; status?: string } } },
) => Promise<EnvelopedFetchResult<KeysetPage<OnboardingPlanRow>>>;

type InstancesGet = (
  path: "/v1/onboarding/instances",
  init: {
    params: { query: { cursor?: string; mine?: boolean; status?: string; employee_id?: string } };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<OnboardingInstanceRow>>>;

type InstanceGet = (
  path: "/v1/onboarding/instances/{id}",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<OnboardingInstanceDetail>>;

// --------------------------------------------------------------------------
// Queries.
// --------------------------------------------------------------------------

export async function fetchOnboardingPlanPage(
  cursor: string | null,
  status?: string,
): Promise<KeysetPage<OnboardingPlanRow>> {
  const get = platformGraph.GET as unknown as PlansGet;
  return unwrapEnvelope(
    get("/v1/onboarding/plans", {
      params: {
        query: {
          ...(cursor !== null && { cursor }),
          ...(status !== undefined && { status }),
        },
      },
    }),
  );
}

export interface InstancePageFilter {
  /** Ask the server for the caller's OWN instances. The server owns the narrow
   *  (an employee_self caller is forced narrow either way); this stops a
   *  manager's self surface from listing their whole subtree. */
  readonly mine?: boolean;
  /** Lifecycle statuses to include. Sent as one comma-separated value — the
   *  route parses a SET so the hub's tabs can partition the lifecycle without
   *  leaving a status reachable from no tab. */
  readonly statuses?: readonly string[];
  /** One subject's instances (the personnel page). Narrows within the server's scope. */
  readonly employeeId?: string;
}

export async function fetchOnboardingInstancePage(
  cursor: string | null,
  filter: InstancePageFilter = {},
): Promise<KeysetPage<OnboardingInstanceRow>> {
  const get = platformGraph.GET as unknown as InstancesGet;
  return unwrapEnvelope(
    get("/v1/onboarding/instances", {
      params: {
        query: {
          ...(cursor !== null && { cursor }),
          ...(filter.mine === true && { mine: true }),
          ...(filter.employeeId !== undefined && { employee_id: filter.employeeId }),
          ...(filter.statuses !== undefined &&
            filter.statuses.length > 0 && { status: filter.statuses.join(",") }),
        },
      },
    }),
  );
}

type PlanGet = (
  path: "/v1/onboarding/plans/{id}",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<OnboardingPlanDetail>>;

export async function fetchOnboardingPlan(id: string): Promise<OnboardingPlanDetail> {
  const get = platformGraph.GET as unknown as PlanGet;
  return unwrapEnvelope(get("/v1/onboarding/plans/{id}", { params: { path: { id } } }));
}

export async function fetchOnboardingInstance(id: string): Promise<OnboardingInstanceDetail> {
  const get = platformGraph.GET as unknown as InstanceGet;
  return unwrapEnvelope(get("/v1/onboarding/instances/{id}", { params: { path: { id } } }));
}

type PlanMatchesGet = (
  path: "/v1/onboarding/plan-matches",
  init: { params: { query: { employee_id: string; management_level?: ManagementLevel } } },
) => Promise<EnvelopedFetchResult<PlanMatchResult>>;

/** Which published plans the hire's department + management level select.
 *  Advisory — the start call still sends an explicit plan_id. */
export async function fetchPlanMatches(
  employeeId: string,
  managementLevel?: ManagementLevel,
): Promise<PlanMatchResult> {
  const get = platformGraph.GET as unknown as PlanMatchesGet;
  return unwrapEnvelope(
    get("/v1/onboarding/plan-matches", {
      params: {
        query: {
          employee_id: employeeId,
          ...(managementLevel !== undefined && { management_level: managementLevel }),
        },
      },
    }),
  );
}

// --------------------------------------------------------------------------
// Task transitions.
//
// The two transitions take DIFFERENT routes and that is the server's design,
// not an inconsistency: completion is spec 19's declared `idempotency: required`
// endpoint (POST .../complete) because a retried completion must replay rather
// than re-run the rollup; skipping rides the generic PATCH, which carries no
// idempotency contract.
// --------------------------------------------------------------------------

/** Fresh key per call (spec `idempotency: required` — never reused across retries). */
function idempotencyHeader(): Record<string, string> {
  return { "x-idempotency-key": crypto.randomUUID() };
}

export interface TaskTransitionResult {
  readonly task: OnboardingTaskRow;
  readonly instance: { readonly completion_percent: string; readonly at_risk: boolean; readonly status: string };
}

type PostComplete = (
  path: "/v1/onboarding/tasks/{id}/complete",
  init: { params: { path: { id: string } }; body: Record<string, never>; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<TaskTransitionResult>>;

type PatchTask = (
  path: "/v1/onboarding/tasks",
  init: { body: { task_id: string; status: string; skipped_reason?: string } },
) => Promise<EnvelopedFetchResult<TaskTransitionResult>>;

// --------------------------------------------------------------------------
// Plan authoring.
// --------------------------------------------------------------------------

/** The create/patch body — `unknown` for the blueprint arrays would defeat the
 *  point; plan-draft.ts owns the exact shape and this mirrors it. */
export interface PlanWriteBody {
  readonly name: string;
  readonly description?: string;
  readonly ramp_window_days: number;
  readonly buddy_policy: string;
  readonly buddy_automation?: BuddyAutomationConfig | null;
  readonly selection_criteria: PlanSelectionCriteria;
  readonly precedence: number;
  readonly milestones: readonly OnboardingMilestone[];
  readonly task_specs: readonly OnboardingTaskSpecWire[];
}

type PostPlan = (
  path: "/v1/onboarding/plans",
  init: { body: PlanWriteBody; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ plan: OnboardingPlanRow }>>;

type PatchPlan = (
  path: "/v1/onboarding/plans/{id}",
  init: { params: { path: { id: string } }; body: PlanWriteBody },
) => Promise<EnvelopedFetchResult<{ plan: OnboardingPlanRow }>>;

type PostPublish = (
  path: "/v1/onboarding/plans/{id}/publish",
  init: {
    params: { path: { id: string } };
    body: Record<string, never>;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ plan: OnboardingPlanRow; version: number }>>;

export async function createOnboardingPlan(body: PlanWriteBody): Promise<OnboardingPlanRow> {
  const post = platformGraph.POST as unknown as PostPlan;
  const result = await unwrapEnvelope(
    post("/v1/onboarding/plans", { body, headers: idempotencyHeader() }),
  );
  return result.plan;
}

export async function updateOnboardingPlan(
  id: string,
  body: PlanWriteBody,
): Promise<OnboardingPlanRow> {
  const patch = platformGraph.PATCH as unknown as PatchPlan;
  const result = await unwrapEnvelope(
    patch("/v1/onboarding/plans/{id}", { params: { path: { id } }, body }),
  );
  return result.plan;
}

/** Publishing freezes the blueprint: the server refuses further PATCHes, which
 *  is why the editor sends every field on save rather than a diff — a published
 *  plan is replaced by a new draft, never edited in place. */
export async function publishOnboardingPlan(id: string): Promise<OnboardingPlanRow> {
  const post = platformGraph.POST as unknown as PostPublish;
  const result = await unwrapEnvelope(
    post("/v1/onboarding/plans/{id}/publish", {
      params: { path: { id } },
      body: {},
      headers: idempotencyHeader(),
    }),
  );
  return result.plan;
}

type PostRetire = (
  path: "/v1/onboarding/plans/{id}/retire",
  init: {
    params: { path: { id: string } };
    body: { reason: string };
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ plan: OnboardingPlanRow }>>;

/** The reason has no column server-side; it survives only on the outbox event,
 *  which is why the route still refuses an empty one. The key is the caller's
 *  so a retried confirm replays instead of failing on the retired state. */
export async function retireOnboardingPlan(
  id: string,
  reason: string,
  idempotencyKey: string,
): Promise<OnboardingPlanRow> {
  const post = platformGraph.POST as unknown as PostRetire;
  const result = await unwrapEnvelope(
    post("/v1/onboarding/plans/{id}/retire", {
      params: { path: { id } },
      body: { reason },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.plan;
}

export type InstanceTransition = "pause" | "resume" | "cancel" | "complete";

type PostInstanceTransition = (
  path: `/v1/onboarding/instances/{id}/${InstanceTransition}`,
  init: {
    params: { path: { id: string } };
    body: { reason?: string; paused_until?: string };
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ instance: OnboardingInstanceRow }>>;

/** Cancel requires `reason`; pause takes an optional `paused_until`
 *  (YYYY-MM-DD); resume and complete take an empty body. */
export async function transitionOnboardingInstance(
  id: string,
  action: InstanceTransition,
  body: { reason?: string; paused_until?: string },
  idempotencyKey: string,
): Promise<OnboardingInstanceRow> {
  const post = platformGraph.POST as unknown as PostInstanceTransition;
  const result = await unwrapEnvelope(
    post(`/v1/onboarding/instances/{id}/${action}`, {
      params: { path: { id } },
      body,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.instance;
}

export interface StartInstanceInput {
  readonly plan_id: string;
  readonly employee_id: string;
  readonly manager_id: string;
  /** YYYY-MM-DD. The whole task timeline is materialized from offsets against
   *  this date, so it is the one field with no sensible default. */
  readonly start_date: string;
}

type PostInstance = (
  path: "/v1/onboarding/instances",
  init: { body: StartInstanceInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ instance: OnboardingInstanceRow; task_count?: number }>>;

export async function startOnboardingInstance(
  input: StartInstanceInput,
  idempotencyKey: string,
): Promise<OnboardingInstanceRow> {
  const post = platformGraph.POST as unknown as PostInstance;
  // The key is the CALLER's, not a fresh one per attempt: starting an
  // onboarding twice for the same hire is the mistake this endpoint's
  // idempotency exists to prevent, and a per-call key would defeat it on a
  // double-click or a retried submit.
  const result = await unwrapEnvelope(
    post("/v1/onboarding/instances", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.instance;
}

export async function completeOnboardingTask(taskId: string): Promise<TaskTransitionResult> {
  const post = platformGraph.POST as unknown as PostComplete;
  return unwrapEnvelope(
    post("/v1/onboarding/tasks/{id}/complete", {
      params: { path: { id: taskId } },
      body: {},
      headers: idempotencyHeader(),
    }),
  );
}

/**
 * Open a task the fan-out left `scheduled`.
 *
 * This is a separate action and not a hidden first half of "complete" because
 * the server's state machine has no `scheduled → completed` edge, and nothing
 * flips a task to `active` on its own — the opens_at worker does not exist. So
 * the screen shows the step the machine actually requires instead of firing two
 * requests behind one button and reporting a single outcome for both.
 */
export async function activateOnboardingTask(taskId: string): Promise<TaskTransitionResult> {
  const patch = platformGraph.PATCH as unknown as PatchTask;
  return unwrapEnvelope(
    patch("/v1/onboarding/tasks", { body: { task_id: taskId, status: "active" } }),
  );
}

/** `reason` is not client-side politeness: the server rejects a reasonless skip
 *  with a field validation error, because a skipped compliance step with no
 *  recorded why is an audit hole. */
export async function skipOnboardingTask(
  taskId: string,
  reason: string,
): Promise<TaskTransitionResult> {
  const patch = platformGraph.PATCH as unknown as PatchTask;
  return unwrapEnvelope(
    patch("/v1/onboarding/tasks", {
      body: { task_id: taskId, status: "skipped", skipped_reason: reason },
    }),
  );
}
